/**
 * Access Control Context
 * Provides centralized permission and role management across the application
 * Uses React Context API for state management (consistent with existing patterns)
 * 
 * @author JGS Tech Team
 * @version 1.0
 */

import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
    ReactNode,
} from 'react';
import type {
    AccessControlState,
    AccessControlContextValue,
    Permission,
    UserRole,
} from '../types/accessControl';
import { accessControlService } from '../services/accessControlService';
import api from '../helpers/axios';

interface ValidateUserResponse {
    roles?: string[];
    permissions?: string[];
}

// ==================== Initial State ====================

const initialState: AccessControlState = {
    roles: [],
    permissions: new Set<string>(),
    permissionDetails: {},
    isLoading: false,
    error: null,
    lastRefreshed: null,
};

// ==================== Context Creation ====================

const AccessControlContext = createContext<AccessControlContextValue | undefined>(undefined);

// ==================== Provider Props ====================

interface AccessControlProviderProps {
    children: ReactNode;
}

// ==================== Provider Component ====================

/**
 * Access Control Provider
 * Wraps the application to provide permission context
 */
export const AccessControlProvider: React.FC<AccessControlProviderProps> = ({ children }) => {
    const [state, setState] = useState<AccessControlState>(initialState);
    // Tracks whether the initial server-side auth check is still in flight.
    // The app MUST NOT render protected UI until this is false.
    const [isInitializing, setIsInitializing] = useState<boolean>(true);

    // ==================== Permission Checking Methods ====================

    /**
     * Check if user has a specific permission
     * Supports wildcard matching (e.g., 'articles:*:all' matches 'articles:view:all')
     */
    const hasPermission = useCallback(
        (permissionCode: string): boolean => {
            if (!permissionCode) return false;
            
            // Direct match
            if (state.permissions.has(permissionCode)) {
                return true;
            }

            // Wildcard matching
            const parts = permissionCode.split(':');
            if (parts.length === 3) {
                // Check for resource:*:scope pattern
                const wildcardPermission = `${parts[0]}:*:${parts[2]}`;
                if (state.permissions.has(wildcardPermission)) {
                    return true;
                }

                // Check for resource:action:* pattern
                const scopeWildcard = `${parts[0]}:${parts[1]}:*`;
                if (state.permissions.has(scopeWildcard)) {
                    return true;
                }

                // Check for resource:*:* pattern
                const fullWildcard = `${parts[0]}:*:*`;
                if (state.permissions.has(fullWildcard)) {
                    return true;
                }

                // Check for *:*:* (superuser)
                if (state.permissions.has('*:*:*')) {
                    return true;
                }
            }

            return false;
        },
        [state.permissions]
    );

    /**
     * Check if user has all specified permissions
     */
    const hasAllPermissions = useCallback(
        (permissionCodes: string[]): boolean => {
            if (!permissionCodes || permissionCodes.length === 0) return true;
            return permissionCodes.every((code) => hasPermission(code));
        },
        [hasPermission]
    );

    /**
     * Check if user has any of the specified permissions
     */
    const hasAnyPermission = useCallback(
        (permissionCodes: string[]): boolean => {
            if (!permissionCodes || permissionCodes.length === 0) return true;
            return permissionCodes.some((code) => hasPermission(code));
        },
        [hasPermission]
    );

    /**
     * Check if user has a specific role
     */
    const hasRole = useCallback(
        (role: UserRole | string): boolean => {
            if (!role) return false;
            const normalizedRole = role.startsWith('ROLE_') ? role : `ROLE_${role.toUpperCase()}`;
            return state.roles.includes(normalizedRole);
        },
        [state.roles]
    );

    /**
     * Check if user has any of the specified roles
     */
    const hasAnyRole = useCallback(
        (roles: (UserRole | string)[]): boolean => {
            if (!roles || roles.length === 0) return true;
            return roles.some((role) => hasRole(role));
        },
        [hasRole]
    );

    /**
     * Check if user is admin
     */
    const isAdmin = useCallback((): boolean => {
        return hasRole('ROLE_ADMIN');
    }, [hasRole]);

    /**
     * Check if user is authenticated (has at least one role)
     */
    const isAuthenticated = useMemo((): boolean => {
        return state.roles.length > 0;
    }, [state.roles]);

    // ==================== State Management Methods ====================

    /**
     * Refresh permissions from server
     */
    const refreshPermissions = useCallback(async (): Promise<void> => {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        try {
            const response = await accessControlService.getMyPermissions();
            
            // Normalize roles to always have ROLE_ prefix for consistent comparison
            const normalizedRoles = (response.roles || []).map((role: string) => 
                role.startsWith('ROLE_') ? role : `ROLE_${role.toUpperCase()}`
            );

            setState({
                roles: normalizedRoles,
                permissions: new Set(response.effectivePermissions || []),
                permissionDetails: response.permissionDetails || {},
                isLoading: false,
                error: null,
                lastRefreshed: Date.now(),
            });
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to fetch permissions';

            setState((prev) => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));

            console.error('Failed to refresh permissions:', error);
        }
    }, []);

    /**
     * Initial authentication check — runs once on app startup.
     * Validates the HTTP-only cookie session against the server.
     * Sets isInitializing to false when done regardless of result.
     */
    useEffect(() => {
        api.get<ValidateUserResponse>('/api/auth/validateUser')
            .then((response) => {
                if (response.data) {
                    setPermissionsFromLogin(
                        response.data.roles || [],
                        response.data.permissions || []
                    );
                }
            })
            .catch(() => {
                // Not authenticated — state stays empty (roles: []), isAuthenticated stays false
            })
            .finally(() => {
                setIsInitializing(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Clear all access control state (on logout)
     */
    const clearAccessControl = useCallback((): void => {
        setState(initialState);
    }, []);

    /**
     * Set permissions from login response
     * Called immediately after login with data from login API
     */
    const setPermissionsFromLogin = useCallback(
        (roles: string[], permissions: string[]): void => {
            // Normalize roles to always have ROLE_ prefix for consistent comparison
            const normalizedRoles = (roles || []).map(role => 
                role.startsWith('ROLE_') ? role : `ROLE_${role.toUpperCase()}`
            );
            
            setState({
                roles: normalizedRoles,
                permissions: new Set(permissions || []),
                permissionDetails: {},
                isLoading: false,
                error: null,
                lastRefreshed: Date.now(),
            });
        },
        []
    );

    // ==================== Context Value ====================

    const contextValue = useMemo<AccessControlContextValue>(
        () => ({
            ...state,
            isInitializing,
            hasPermission,
            hasAllPermissions,
            hasAnyPermission,
            hasRole,
            hasAnyRole,
            isAdmin,
            isAuthenticated,
            refreshPermissions,
            clearAccessControl,
            setPermissionsFromLogin,
        }),
        [
            state,
            isInitializing,
            hasPermission,
            hasAllPermissions,
            hasAnyPermission,
            hasRole,
            hasAnyRole,
            isAdmin,
            isAuthenticated,
            refreshPermissions,
            clearAccessControl,
            setPermissionsFromLogin,
        ]
    );

    return (
        <AccessControlContext.Provider value={contextValue}>
            {children}
        </AccessControlContext.Provider>
    );
};

// ==================== Hook ====================

/**
 * useAccessControl hook
 * Access the access control context from any component
 */
export const useAccessControl = (): AccessControlContextValue => {
    const context = useContext(AccessControlContext);

    if (context === undefined) {
        throw new Error('useAccessControl must be used within an AccessControlProvider');
    }

    return context;
};

// ==================== Export ====================

export { AccessControlContext };
export default AccessControlProvider;
